/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: State
 * Reason: TM-1000002000 — the ONE source of the consultant's "noch nicht erfasst" badge count.
 *         Counts the Vorgänge of the logged-on agent that are in one of
 *         {@code CW_CONSULTANT_UNCAPTURED_PHASES} (QUICK_QUOTE, DRAFT) among the rows the
 *         Consultant shell's own Vorgang table ("Laufende Anträge") already shows, and is read by
 *         the menu entry that leads there. Same shape as the (since-removed, BR-2) Antrag draft
 *         counter of TM-1000002001 — agent resolution, debounce, switchMap-latest-wins,
 *         BehaviorSubject publishing.
 */
import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {BehaviorSubject, Observable, Subject, of} from 'rxjs';
import {catchError, debounceTime, filter, map, switchMap} from 'rxjs/operators';
import {AgentService} from '../../../feature-core/am/service/api/agent.service';
import {PagingDto} from '../../../core/shared/dto/model/paging.dto';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {QlRequestDto} from '../../../core/shared/ql/data/dto/ql-request.dto';
import {QlService} from '../../../core/shared/ql/service/ql.service';
import {MvsObjectService} from '../../../core/shared/service/object/mvs-object.service';
import {buildCwConsultantCaseTableWidget} from '../widget/cw-case-mine.widget';
import {CW_CONSULTANT_UNCAPTURED_PHASES} from '../util/cw-process-phase.util';

/**
 * Object type alias of {@code cw.CwCase} — the filter of the change-bus subscription.
 *
 * @ticket TM-1000002000
 */
export const CW_CASE_OBJECT_TYPE = 'cw.CwCase';

/**
 * Query identity of the count — its own name, like every other surface's query.
 *
 * @ticket TM-1000002000
 */
export const CW_CASE_UNCAPTURED_COUNT_QUERY = 'cw.case.mine.uncaptured.count';

/**
 * The severity of the "noch nicht erfasst" marker on the consultant navigation's menu entry.
 *
 * <p>A severity and never a colour: a hex value would be wrong in dark mode and could not be changed
 * for every badge of this shell at once.
 *
 * @ticket TM-1000002000
 */
export const CW_CASE_UNCAPTURED_MARKER_SEVERITY = 'danger' as const;

/**
 * Coalescing window of the change bus / navigation stream. A user action can fire more than one
 * signal in quick succession (a write on the bus followed almost immediately by the resulting
 * navigation), and without the window the counter would issue two reads for one action.
 *
 * @ticket TM-1000002000
 */
const CW_CASE_UNCAPTURED_COUNT_DEBOUNCE_MS = 300;

/**
 * The ONE source of the consultant's "noch nicht erfasst" badge count — read by the menu entry that
 * leads to the Consultant shell's Vorgang table.
 *
 * <h3>The count is a STRICT SUBSET of the table it badges, by construction</h3>
 * {@link buildCountQuery} does not restate the table's rule. It calls the SAME entry point the table
 * itself is built from — {@code buildCwConsultantCaseTableWidget}
 * ({@code cw-my-requests.component.ts}) — passing ONLY one further narrowing on top of what that
 * function already bakes in (the ownership union, the {@code entityStatus} guard, the closed-phase
 * exclusion): {@link CW_CONSULTANT_UNCAPTURED_PHASES} as an {@code includedPhases} {@code IN}
 * criterion. It then lifts the resulting query and only swaps the projection budget (one row) for
 * the count. Because both call sites share the SAME function rather than each restating its own
 * column set/exclusion, the two cannot silently diverge (compliance finding 1) — a change to the
 * table's ownership rule or its closed-phase exclusion changes the badge with it by construction.
 *
 * <h3>Refresh: the change bus AND navigation, never a heartbeat (D7)</h3>
 * {@link refresh} runs once when a consumer is built and afterwards whenever EITHER of two signals
 * fires:
 * <ul>
 *   <li>the platform's object-change bus ({@code MvsObjectService.objectChangeEvent}) reports a
 *       write on {@code cw.CwCase} — the same channel {@code mvs-widget} refreshes its tables
 *       from;</li>
 *   <li>{@code Router.events} emits {@link NavigationEnd} — because the bus is SILENT on the
 *       {@code postGeneric} actions that move a Vorgang out of this count
 *       ({@code prepareNew}/{@code handOverQuickQuote}/{@code promoteQuickQuote} do not broadcast).
 *       Without this second trigger the badge would not move when a Quick Quote is handed over or
 *       promoted, even though the population it counts just changed.</li>
 * </ul>
 * Both signals share the SAME debounce and {@code switchMap} as the bus-only draft counter, so a
 * write followed immediately by its navigation still costs one request, not two.
 *
 * <h3>BR-8 — never a badge before the agent is known, never a thrown error</h3>
 * No agent, or a {@link CW_CONSULTANT_UNCAPTURED_PHASES} the platform has emptied ->
 * {@link buildCountQuery} returns {@code null} -> {@code 0}, not an error. A QL failure resolves to
 * {@code null} ("not counted") via {@code catchError}, exactly like the draft counter: a badge is
 * decoration, and a broken read must never surface as a toast in the menu. {@code switchMap} (not
 * {@code mergeMap}) on BOTH the outer refresh stream and the inner agent/QL chain means a request
 * that arrives while an older one is still in flight keeps only the newest answer.
 *
 * @ticket TM-1000002000
 */
@Injectable({providedIn: 'root'})
export class CwCaseUncapturedCountService {

    /**
     * The published number. {@code null} means "not counted (yet)" and is NOT zero: both render as
     * "no badge", but only {@code null} may later turn into one without anything having changed.
     */
    private readonly countSubject = new BehaviorSubject<number>(null);

    /** The count the menu entry subscribes to; replays the current value to a late subscriber. */
    readonly count$: Observable<number> = this.countSubject.asObservable();

    /** Refresh requests, coalesced and then read with {@code switchMap} — see the constructor. */
    private readonly refreshRequests = new Subject<void>();

    constructor(
        protected qlService: QlService,
        protected agentService: AgentService,
        protected objectService: MvsObjectService,
        protected router: Router) {

        // switchMap, not mergeMap: a request that arrives while a read is in flight CANCELS it, so
        // the badge always ends on the newest answer rather than whichever of two concurrent reads
        // happens to land last.
        this.refreshRequests
            .pipe(
                debounceTime(CW_CASE_UNCAPTURED_COUNT_DEBOUNCE_MS),
                switchMap(() => this.readCount()),
            )
            .subscribe(count => this.countSubject.next(count));

        this.subscribeToCaseChanges();
        this.subscribeToNavigation();
    }

    /** The current number without subscribing; {@code null} while nothing has been counted. */
    get count(): number {
        return this.countSubject.value;
    }

    /**
     * The severity of the "noch nicht erfasst" marker for a given count, or {@code null} when there
     * is nothing to mark. {@code 0} and {@code null}/{@code undefined} both yield {@code null} — the
     * caller renders no badge for either, and this method is the ONE place that decides so.
     */
    static markerSeverityOf(count: number | null | undefined): typeof CW_CASE_UNCAPTURED_MARKER_SEVERITY | null {
        return count > 0 ? CW_CASE_UNCAPTURED_MARKER_SEVERITY : null;
    }

    /**
     * Asks for a fresh count. Idempotent and cheap to call — requests are coalesced over
     * {@link CW_CASE_UNCAPTURED_COUNT_DEBOUNCE_MS} and a superseded read is cancelled.
     */
    refresh(): void {
        this.refreshRequests.next();
    }

    /** Listens on the platform's object-change bus for writes on {@code cw.CwCase}. */
    private subscribeToCaseChanges(): void {
        this.objectService.objectChangeEvent
            .pipe(filter(change => change?.objectType === CW_CASE_OBJECT_TYPE))
            .subscribe(() => this.refresh());
    }

    /**
     * Listens on {@code Router.events} for {@link NavigationEnd} — the bus-silent half of D7. See
     * the class doc for why this second trigger is load-bearing and not merely defensive.
     */
    private subscribeToNavigation(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => this.refresh());
    }

    /**
     * Resolves the logged-on consultant and counts their not-yet-captured Vorgänge.
     *
     * <p>Never fails loudly — see BR-8 in the class doc.
     */
    private readCount(): Observable<number> {
        return this.agentService.me().pipe(
            switchMap(agent => this.countUncapturedOfAgent(agent?.id)),
            catchError(() => of(null)),
        );
    }

    /** The QL read itself — one row transferred, the number taken from the pipe count. */
    private countUncapturedOfAgent(agentId: number): Observable<number> {

        const query = CwCaseUncapturedCountService.buildCountQuery(agentId);

        // No agent, or a phase set the platform has emptied: 0 rather than null. Both are ANSWERS
        // ("there is nothing to badge"), not failures, and both render as no badge.
        if (!query) {
            return of(0);
        }

        return this.qlService.builder()
            .showErrors(false)
            .query(query)
            // The BUILDER's pipe (export-pipe settings), not the rxjs one below. includeCount is
            // what puts the total into the response at all — without it getCount() answers 0.
            .pipe({
                name: CW_CASE_UNCAPTURED_COUNT_QUERY,
                simpleQuery: CW_CASE_UNCAPTURED_COUNT_QUERY,
                settings: {includeCount: true},
            })
            .execute()
            .pipe(map(result => result.getCount(CW_CASE_UNCAPTURED_COUNT_QUERY) ?? 0));
    }

    /**
     * Builds the count query BY DERIVING it from the table — see the class doc.
     *
     * <p>Calls {@code buildCwConsultantCaseTableWidget} — the SAME entry point
     * {@code cw-my-requests.component.ts} builds the table from — with the badge's OWN further
     * narrowing ({@link CW_CONSULTANT_UNCAPTURED_PHASES} as {@code includedPhases}); the column set
     * and the closed-phase exclusion are NOT passed here because that function already bakes them in
     * for every caller. Only three things are then swapped, none of them a filter: the query name
     * (one identity per surface), the sortings (a count has no order) and the page size (one row —
     * the number comes from the pipe count, not from the payload).
     *
     * @param agentId {@code am.Agent} id of the logged-on consultant.
     * @return the query, or {@code null} when there is nothing to count — no agent, or an uncaptured
     *         set the platform's own rule has emptied.
     */
    static buildCountQuery(agentId: number): QlQueryDto {

        if (!agentId || CW_CONSULTANT_UNCAPTURED_PHASES.size === 0) {
            return null;
        }

        const listWidget = buildCwConsultantCaseTableWidget(agentId, CW_CONSULTANT_UNCAPTURED_PHASES);

        const listRequest = listWidget?.dataProviderListRequest;
        if (!(listRequest instanceof QlRequestDto)) {
            return null;
        }

        const listQuery = listRequest.queries?.[0];
        if (!listQuery) {
            return null;
        }

        return {
            ...listQuery,
            name: CW_CASE_UNCAPTURED_COUNT_QUERY,
            sortings: [],
            paging: PagingDto.createSize(1),
        };
    }

}
